import type {
    SerializationResult,
    SerializedFeature,
} from "@alchemiesolns/feature-extractor";
import { chemicalAtomSymbol } from "./chemicalAtomSymbol";

/**
 * PIPH-212: Classify how a "between"-classified functional group connects two
 * primary features.
 *
 *  - "direct"   — none of the group's heavy (non-H) atoms sit outside
 *                 `primaryA ∪ primaryB`. The two primaries already share the
 *                 bond; the group is just labeling the bond order.
 *                 (Alkene/alkyne between two chains: both C atoms belong to
 *                 the two primaries — no extra atoms form a path.)
 *  - "bridge"   — the group is in `RECOGNIZED_BRIDGING_GROUPS` (ester, ether,
 *                 secondary/tertiary amine, sulfide, acid anhydride). The
 *                 group has its own atoms forming the path between primaries.
 *  - "fallback" — none of the above. The group has heavy atoms outside the
 *                 two primaries, but isn't a recognized bridging group.
 *                 PIPH-138 phrasing: "through {bond} bond to {formula}".
 */
export type PrimaryConnectionKind = "direct" | "bridge" | "fallback";

export interface PrimaryConnectionInfo {
    kind: PrimaryConnectionKind;
    /** The functional group itself. Always populated. */
    group: SerializedFeature;
    /**
     * For "direct": the order of the bond directly connecting an atom of
     * primaryA to an atom of primaryB.
     * For "fallback": the order of the bond from the group's first atom to
     * a primary (used to form "through {single|double|triple} bond to ...").
     */
    bondOrder?: 1 | 2 | 3;
    /**
     * For "fallback": the chemistry-shorthand formula of the bridging group's
     * first atom, e.g. "N", "NH", "NH₂", "O⁻". Used to form
     * "through {bond} bond to {formula}".
     */
    formula?: string;
}

/**
 * PIPH-212 + PIPH-138: Recognized bridging functional groups. These have their
 * own atoms forming the path between primaries, so the connection is described
 * via the group name ("through ester"). Anything else with heavy atoms outside
 * the two primaries falls back to "through {bond} to {formula}".
 *
 * Source-of-truth: this set lives here, not duplicated in each description path.
 */
export const RECOGNIZED_BRIDGING_GROUPS: ReadonlySet<string> = new Set([
    "ether",
    "sulfide",
    "amine.2",
    "amine.3",
    "carbonyl.ester",
    "carbonyl.acidAnhydride",
]);

export function classifyPrimaryConnection(
    group: SerializedFeature,
    primaryA: SerializedFeature,
    primaryB: SerializedFeature,
    result: SerializationResult,
): PrimaryConnectionInfo {
    // Build the union of the two primaries' atoms for fast lookup.
    const unionAtoms = new Set<number>();
    for (const idx of primaryA.atomIndices) unionAtoms.add(idx);
    for (const idx of primaryB.atomIndices) unionAtoms.add(idx);

    // Look up atom symbols (skip hydrogens — PIPH-106 rule).
    const symbolByIndex = new Map<number, string>();
    for (const atom of result.atoms) symbolByIndex.set(atom.index, atom.symbol);

    // Are any of the group's HEAVY atoms outside the union of the two primaries?
    let hasOutsideHeavy = false;
    for (const ai of group.atomIndices) {
        if (symbolByIndex.get(ai) === "H") continue;
        if (!unionAtoms.has(ai)) {
            hasOutsideHeavy = true;
            break;
        }
    }

    if (!hasOutsideHeavy) {
        // PIPH-212: every heavy atom of the group sits inside one of the two
        // primaries. There is no bridge — the two primaries are connected
        // directly, and the group only describes the bond order. Find the
        // direct inter-primary bond.
        const aAtoms = new Set(primaryA.atomIndices);
        const bAtoms = new Set(primaryB.atomIndices);
        let bondOrder: 1 | 2 | 3 = 1;
        for (const bond of result.bonds) {
            const [x, y] = bond.atomIndices;
            const xInA = aAtoms.has(x);
            const yInB = bAtoms.has(y);
            const xInB = bAtoms.has(x);
            const yInA = aAtoms.has(y);
            if ((xInA && yInB) || (xInB && yInA)) {
                bondOrder = (bond.order === 2 || bond.order === 3 ? bond.order : 1);
                break;
            }
        }
        return { kind: "direct", group, bondOrder };
    }

    if (group.internalName && RECOGNIZED_BRIDGING_GROUPS.has(group.internalName)) {
        return { kind: "bridge", group };
    }

    // Fallback (PIPH-138): unrecognized bridging atoms. Pick a representative
    // atom of the group and the bond from it that touches one of the primaries.
    const primaryAtoms = unionAtoms;
    let bondOrder: 1 | 2 | 3 = 1;
    let representativeAtomIdx: number | undefined;
    for (const ai of group.atomIndices) {
        if (symbolByIndex.get(ai) === "H") continue;
        if (primaryAtoms.has(ai)) continue;
        // This atom is outside both primaries.
        const atom = result.atoms.find(a => a.index === ai);
        if (!atom) continue;
        for (const bondIndex of atom.bondIndices) {
            const bond = result.bonds[bondIndex];
            if (!bond) continue;
            const otherIdx = bond.atomIndices[0] === ai
                ? bond.atomIndices[1]
                : bond.atomIndices[0];
            if (primaryAtoms.has(otherIdx)) {
                bondOrder = (bond.order === 2 || bond.order === 3 ? bond.order : 1);
                if (representativeAtomIdx === undefined) representativeAtomIdx = ai;
                break;
            }
        }
        if (representativeAtomIdx !== undefined) break;
    }
    if (representativeAtomIdx === undefined) {
        // Couldn't find an outside atom touching a primary — extremely unusual
        // (the caller already established the group is "between"). Fall back
        // to the first heavy atom of the group.
        representativeAtomIdx = group.atomIndices.find(
            ai => symbolByIndex.get(ai) !== "H",
        );
    }
    const formula = representativeAtomIdx !== undefined
        ? formatAtomFormula(representativeAtomIdx, result)
        : group.name;

    return { kind: "fallback", group, bondOrder, formula };
}

/**
 * Format an atom as chemistry shorthand: element + implicit Hs + charge.
 * Examples: "N", "NH", "NH₂", "O⁻", "NH₂⁺".
 */
function formatAtomFormula(atomIndex: number, result: SerializationResult): string {
    const atom = result.atoms.find(a => a.index === atomIndex);
    if (!atom) return "";
    let formula = chemicalAtomSymbol(atom.symbol, atom.hydrogenCount);
    if (atom.charge !== 0) {
        const chargeSymbol = atom.charge > 0 ? "⁺" : "⁻";
        const mag = Math.abs(atom.charge);
        if (mag > 1) {
            const sup = "⁰¹²³⁴⁵⁶⁷⁸⁹";
            const digits = String(mag)
                .split("")
                .map(d => sup[parseInt(d, 10)] ?? d)
                .join("");
            formula += digits + chargeSymbol;
        } else {
            formula += chargeSymbol;
        }
    }
    return formula;
}
